// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HeaderClickable from "@conklin/App/Components/Header/HeaderClickable";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import Link from "@conklin/MobiusOverrides/Link/Link";
import IconHeaderHeadset from "@conklin/XDL/Icons/IconHeaderHeadset";
import { safeJsonParse } from "@conklin/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { withTheme } from "styled-components";
import FloatingTooltip from "@conklin/App/Components/FloatingTooltip/FloatingTooltip";

const enum fields {
    contactUsUrl = "contactUsUrl",
    support = "support",
}

interface OwnProps extends WidgetProps {
    theme: BaseTheme;
    fields: {
        [fields.contactUsUrl]: string;
        [fields.support]: SupportModel[];
    };
}
interface SupportModel {
    fields: {
        title: string;
        phone: string;
        hours: string;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    isTabletScreen: state.xCommon.isTabletScreen,
    userName: state.context.session?.userName || "",
    currentUserIsGuest: getCurrentUserIsGuest(state),
});

export interface HeaderMyRepStyles {
    repMainTitle?: TypographyPresentationProps;
    row?: InjectableCss;
    repWrapper?: InjectableCss;
    primaryRepContainer?: InjectableCss;
    secondaryRepContainer?: InjectableCss;
    repSupportContainer?: InjectableCss;
    repHeader?: TypographyPresentationProps;
    repDetails?: InjectableCss;
    repBody?: InjectableCss;
    repImage?: LazyImageProps;
    repName?: TypographyPresentationProps;
    repTitle?: TypographyPresentationProps;
    repContact?: InjectableCss;
    icon?: IconProps;
    repLink?: LinkPresentationProps;
    repHours?: TypographyPresentationProps;
    supportName?: TypographyPresentationProps;
}

export const headerMyRepStyles: HeaderMyRepStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
const styles = headerMyRepStyles;

export const HeaderMyRepComponent = ({ fields, showTitle }: any) => {
    const { primarySalesRep, secondarySalesRep } = useAppSelector(state => {
        const session = getSession(state);

        return {
            primarySalesRep: safeJsonParse(session.properties?.primarySalesRep),
            secondarySalesRep: safeJsonParse(session.properties?.secondarySalesRep) || undefined,
        };
    });

    return (
        <StyledWrapper {...styles.repWrapper}>
            {showTitle && (
                <StyledWrapper {...styles.repHeader}>
                    <Typography {...styles.repMainTitle}>{translate("Reps & Support")}</Typography>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.repBody}>
                {primarySalesRep && (
                    <StyledWrapper {...styles.primaryRepContainer}>
                        <LazyImage
                            src={primarySalesRep.ProfileImage}
                            {...styles.repImage}
                            altText="Primary Sales Rep Image"
                        />
                        <StyledWrapper {...styles.repDetails}>
                            <Typography {...styles.repTitle}>{siteMessage("Custom_My_Rep1_Title")}</Typography>
                            <StyledWrapper {...styles.repContact}>
                                <Typography {...styles.repName}>{primarySalesRep.Name}</Typography>
                                {!!primarySalesRep.PhoneNumber && (
                                    <Link {...styles.repLink} href={`tel:${primarySalesRep.PhoneNumber}`}>
                                        {primarySalesRep.PhoneNumber}
                                    </Link>
                                )}
                                {!!primarySalesRep.Email && (
                                    <Link {...styles.repLink} href={`mailto:${primarySalesRep.Email}`}>
                                        {primarySalesRep.Email}
                                    </Link>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                )}

                {secondarySalesRep && (
                    <StyledWrapper {...styles.secondaryRepContainer}>
                        <LazyImage src={secondarySalesRep.ProfileImage} {...styles.repImage} />
                        <StyledWrapper {...styles.repDetails}>
                            <Typography {...styles.repTitle}>{siteMessage("Custom_My_Rep2_Title")}</Typography>
                            <StyledWrapper {...styles.repContact}>
                                <Typography {...styles.repName}>{secondarySalesRep.Name}</Typography>
                                {!!secondarySalesRep.PhoneNumber && (
                                    <Link {...styles.repLink} href={`tel:${secondarySalesRep.PhoneNumber}`}>
                                        {secondarySalesRep.PhoneNumber}
                                    </Link>
                                )}
                                {!!secondarySalesRep.Email && (
                                    <Link {...styles.repLink} href={`mailto:${secondarySalesRep.Email}`}>
                                        {secondarySalesRep.Email}
                                    </Link>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                )}

                {fields.support.map((value: any, index: number) => (
                    <StyledWrapper key={index} {...styles.repSupportContainer}>
                        <Typography {...styles.supportName}>{value.fields.title}</Typography>
                        <Link {...styles.repLink} href={`tel:${value.fields.phone}`}>
                            {value.fields.phone}
                        </Link>
                        <Typography {...styles.repHours}>{value.fields.hours}</Typography>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const HeaderMyRep: FC<Props> = ({ isTabletScreen, fields, currentUserIsGuest, userName }) => {
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const history = useHistory();

    if (!userName || currentUserIsGuest) {
        return null;
    }

    const openContactUsPageHandler = () => {
        if (fields.contactUsUrl) {
            history.push(fields.contactUsUrl);
        }
    };

    const headerClickable = (
        <HeaderClickable
            iconProps={{
                src: IconHeaderHeadset,
            }}
            data-test-selector="myRep"
            title={translate("My Rep")}
            isOpen={isPopupShown}
            onClick={openContactUsPageHandler}
        />
    );

    if (isTabletScreen) {
        return headerClickable;
    }

    return (
        <FloatingTooltip
            triggerComponent={headerClickable}
            offset={{ crossAxis: 0, mainAxis: 6 }}
            placement="bottom-end"
            onOpenChange={open => {
                setIsPopupShown(open);
            }}
            showArrow={false}
        >
            <HeaderMyRepComponent fields={fields} showTitle={true} />
        </FloatingTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withTheme(HeaderMyRep)),
    definition: {
        displayName: "Header My Sale Rep",
        fieldDefinitions: [
            {
                name: fields.contactUsUrl,
                displayName: "Link URL",
                editorTemplate: "TextField",
                defaultValue: "ContactUs",
                fieldType: "General",
            },
            {
                name: fields.support,
                displayName: "Support",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Translatable",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldDefinitions: [
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "phone",
                        displayName: "Support Phone",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "hours",
                        displayName: "Working HOurs",
                        editorTemplate: "TextField",
                        defaultValue: "Mon-Fri 8am – 5pm ET",
                    },
                ],
            },
        ],
        group: "Common",
        allowedContexts: ["Header"],
    },
};

export default widgetModule;
